/**
 * djLibraryService — search the DJ's FULL desktop music library from the PWA.
 *
 * The desktop app publishes a lightweight index of every track on its local
 * drive to Firestore (`users/{venueUid}/libraryIndex/page_N`, written by
 * publishLibraryIndex in the desktop's library-functions.js). This service
 * lazily fetches that index the first time a guest searches, caches it in
 * memory, and does substring matching on title + artist.
 *
 * Purpose: a guest searching "Whitney Houston" finds the DJ's local tracks
 * even when none are in the currently loaded playlist — shown ABOVE the
 * YouTube results. Never throws; returns [] on any failure.
 *
 * Index track shape (minified by the desktop): { id, t: title, a: artist, g: genre }
 */

import { db } from "../firebase-config";
import { collection, getDocs } from "firebase/firestore";

const CACHE_TTL_MS = 10 * 60 * 1000; // re-fetch the index at most every 10 min
const _cache = new Map(); // venueUid -> { tracks, at }
let _inflight = null;

async function loadIndex(venueUid) {
  const hit = _cache.get(venueUid);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.tracks;
  if (_inflight) return _inflight; // collapse concurrent keystroke fetches

  _inflight = (async () => {
    try {
      const snap = await getDocs(collection(db, "users", venueUid, "libraryIndex"));
      const tracks = [];
      snap.forEach((d) => {
        if (d.id.startsWith("page_")) {
          const arr = d.data().tracks;
          if (Array.isArray(arr)) for (const tr of arr) tracks.push(tr);
        }
      });
      _cache.set(venueUid, { tracks, at: Date.now() });
      return tracks;
    } catch (_) {
      return (hit && hit.tracks) || [];
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * Substring search over the DJ's library index.
 * @returns {Promise<Array<{id,t,a,g}>>} up to `limit` matches (or []).
 */
export async function searchDjLibrary(venueUid, term, limit = 8) {
  try {
    const q = String(term || "").trim().toLowerCase();
    if (!venueUid || q.length < 3) return [];
    const tracks = await loadIndex(venueUid);
    const out = [];
    for (const tr of tracks) {
      const t = (tr.t || "").toLowerCase();
      const a = (tr.a || "").toLowerCase();
      if (t.includes(q) || a.includes(q) || `${a} ${t}`.includes(q) || `${t} ${a}`.includes(q)) {
        out.push(tr);
        if (out.length >= limit) break;
      }
    }
    return out;
  } catch (_) {
    return [];
  }
}
